import { useEffect, useRef, useState } from 'react';
import { useTopicFetching } from './useTopicFetching';

/**
 * A unified hook for managing topic skeleton loading states
 * across different components.
 *
 * Improved to utilize the project cache system for smoother project switching:
 * - Avoids showing skeletons for already loaded projects
 * - Still shows skeletons on first application load
 * - Shows skeletons during actual data fetching for uncached projects only
 */
export function useTopicSkeletonState(projectId: string | null) {
  // Use a ref to track if this is the first app mount
  const isAppFirstMount = useRef(true);

  // Track when project IDs change to avoid false positives
  const previousProjectIdRef = useRef<string | null>(null);

  // State to track if we should force-disable skeletons for this project
  const [forceDisableSkeleton, setForceDisableSkeleton] = useState(false);

  // Use the shared hook for topic fetching which includes cache tracking
  const { loading: fetchLoading, topics, hasProjectCache } = useTopicFetching(projectId);

  // Effect to handle project changes
  useEffect(() => {
    if (!projectId) return;

    // When project changes, check if we should disable skeletons
    if (projectId !== previousProjectIdRef.current) {
      // If this project is cached, immediately disable skeletons
      if (hasProjectCache) {
        setForceDisableSkeleton(true);
      } else {
        setForceDisableSkeleton(false);
      }

      // Update the previous project ref
      previousProjectIdRef.current = projectId;
    }
  }, [projectId, hasProjectCache]);

  // Calculate if we should show the skeleton with our improved logic:
  // 1. If force disable is active (cached project), never show skeleton
  // 2. If this is first app mount and no topics, show skeleton (but only if not cached)
  // 3. If loading uncached project data, show skeleton
  const showSkeleton = forceDisableSkeleton
    ? false
    : (isAppFirstMount.current && topics.length === 0 && !hasProjectCache) ||
      (fetchLoading && !hasProjectCache);

  // Clear the first mount flag when appropriate
  useEffect(() => {
    if (isAppFirstMount.current) {
      // Turn off first mount flag when we have topics or a project
      if (topics.length > 0 || projectId) {
        isAppFirstMount.current = false;
      }
    }
  }, [projectId, topics.length]);

  return {
    loading: showSkeleton,
  };
}
